import { Link } from '@tanstack/react-router';
import { useAuth } from '@/shared/auth';
import { I } from '@/shared/icons';
import { cn } from '@/shared/lib/cn';
import { settingsNavigation } from './settingsNavigation';
import { isSettingsItemVisible } from './settingsVisibility';

interface SettingsNavProps {
  currentPath: string;
  onNavigate?: () => void;
}

function isActive(currentPath: string, to: string): boolean {
  return currentPath === to || currentPath.startsWith(`${to}/`);
}

/**
 * Interne Sub-Navigation des Einstellungen-Hubs. Rendert nur die Gruppen/Einträge,
 * auf die der User Zugriff hat (`isSettingsItemVisible`) — spiegelt die Route-Guards.
 *
 * <p>Die Zeilenhöhe liegt bei 44 px statt der früheren 34 px: §9 verlangt das als Mindestmaß,
 * und unter `lg` ist diese Liste der einzige Weg zwischen den Einstellungsseiten.</p>
 */
export function SettingsNav({ currentPath, onNavigate }: SettingsNavProps) {
  const { user } = useAuth();

  return (
    <nav aria-label="Einstellungen" className="flex flex-col gap-6">
      {settingsNavigation.map((group) => {
        const visibleItems = group.items.filter((item) => isSettingsItemVisible(user, item));
        if (visibleItems.length === 0) return null;
        return (
          <div key={group.label}>
            <div className="text-ink-faint px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.14em]">
              {group.label}
            </div>
            <ul className="flex flex-col gap-0.5">
              {visibleItems.map((item) => {
                const active = isActive(currentPath, item.to);
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      onClick={onNavigate}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'focus-visible:ring-sage flex min-h-11 items-center gap-2.5 rounded-md px-3 py-2 text-[13.5px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 motion-reduce:transition-none',
                        active
                          ? 'bg-navy text-paper'
                          : 'text-ink-soft hover:bg-card hover:text-ink',
                      )}
                    >
                      <I name={item.icon} size={16} strokeWidth={2} aria-hidden />
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}
